import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import ChangelogModal from 'src/components/modals/ChangelogModal/ChangelogModal';
import { localeDate } from 'src/localization/locale';
import { ChangelogEntry, fetchChangelogPage, markChangelogSeen } from 'src/services/changelog';

const PAGE_SIZE = 20;

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Settings tab listing every release note visible to the user; a click opens it in the modal. */
const WhatsNew: React.FC = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<ChangelogEntry | null>(null);
  const query = useInfiniteQuery({
    queryKey: ['changelog', 'list'],
    queryFn: ({ pageParam = 1 }) => fetchChangelogPage(pageParam as number, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (pages.length * PAGE_SIZE < lastPage.total ? pages.length + 1 : undefined),
  });
  const entries = query.data?.pages.flatMap(page => page.items) ?? [];

  const open = (entry: ChangelogEntry) => {
    setSelected(entry);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('changelog');
        return next;
      },
      { replace: true },
    );
  };

  const close = () => {
    if (selected && !selected.seen) {
      markChangelogSeen(selected.id)
        .then(() => query.refetch())
        .catch(() => undefined);
    }
    setSelected(null);
  };

  return (
    <div className="flex flex-col gap-16 max-w-[900px]">
      <div className="flex flex-col gap-4">
        <h2 className="typo-title-24 text-full">{t('changelog.whats_new')}</h2>
      </div>
      {query.isLoading && <span className="typo-text-14 text-faded">…</span>}
      {!query.isLoading && entries.length === 0 && (
        <span className="typo-text-14 text-faded">{t('changelog.empty')}</span>
      )}
      <div className="flex flex-col gap-12">
        {entries.map(entry => (
          <button
            key={entry.id}
            type="button"
            onClick={() => open(entry)}
            className={classNames(
              'text-left flex items-start gap-16 rounded-base border-subtle surface-elevated card-shadow px-20 py-16 cursor-pointer hover:surface-elevated-darken',
            )}>
            <div className="flex-1 min-w-0 flex flex-col gap-6">
              <div className="flex items-center gap-8">
                <span className="typo-text-16--bold text-full truncate">{entry.title}</span>
                {!entry.seen && <span className="w-[8px] h-[8px] rounded-full status-warning-full-bg shrink-0" />}
              </div>
              <span className="typo-text-12 text-faded">
                {[entry.version, entry.publish_date ? localeDate(entry.publish_date) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <span className="typo-text-14 text-faded line-clamp-2">{stripHtml(entry.body_html)}</span>
            </div>
            <Icon name="chevronRight" width={20} height={20} className="text-faded shrink-0 mt-2" />
          </button>
        ))}
      </div>
      {query.hasNextPage && (
        <div>
          <Button
            tone={ButtonToneProps.SECONDARY}
            title={t('changelog.load_more')}
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          />
        </div>
      )}
      {selected && <ChangelogModal entries={[selected]} isOpen onClose={close} />}
    </div>
  );
};

export default WhatsNew;
